// Authentication API service
import { BaseAPI } from './base';
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  User,
  PasswordResetRequest,
  PasswordResetConfirm,
  ChangePasswordRequest,
  UpdateProfileRequest,
  UserProfilesResponse,
  UserInstitutionsResponse,
  CurrentUserResponse,
} from '../../types/auth';

class AuthAPI extends BaseAPI {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>({
      method: 'POST',
      url: '/api/v1/auth/login',
      data: credentials,
    });
  }

  async register(userData: RegisterRequest): Promise<{
    message: string;
    user_id: string;
    email_sent: boolean;
  }> {
    return this.request<{
      message: string;
      user_id: string;
      email_sent: boolean;
    }>({
      method: 'POST',
      url: '/api/v1/auth/register',
      data: userData,
    });
  }

  async logout(): Promise<void> {
    return this.request<void>({
      method: 'POST',
      url: '/api/v1/auth/logout',
    });
  }

  async getCurrentUser(): Promise<CurrentUserResponse> {
    return this.request<CurrentUserResponse>({
      method: 'GET',
      url: '/api/v1/auth/me',
    });
  }

  async changePassword(data: ChangePasswordRequest): Promise<void> {
    return this.request<void>({
      method: 'POST',
      url: '/api/v1/auth/change-password',
      data,
    });
  }

  async forgotPassword(data: PasswordResetRequest): Promise<void> {
    return this.request<void>({
      method: 'POST',
      url: '/api/v1/auth/forgot-password',
      data,
    });
  }

  async resetPassword(token: string, data: PasswordResetConfirm): Promise<void> {
    return this.request<void>({
      method: 'POST',
      url: `/api/v1/auth/reset-password/${token}`,
      data,
    });
  }

  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    return this.request<User>({
      method: 'POST',
      url: '/api/v1/auth/update-profile',
      data,
    });
  }

  async verifyEmail(token: string): Promise<{ success: boolean; message: string; already_verified?: boolean }> {
    return this.request<{ success: boolean; message: string; already_verified?: boolean }>({
      method: 'GET',
      url: `/api/v1/auth/verify-email/${token}`,
      headers: { Accept: 'application/json' },
      params: { format: 'json' },
    });
  }

  async resendVerification(data: {
    email?: string;
    username?: string;
  }): Promise<{ message: string; email_sent: boolean }> {
    return this.request<{ message: string; email_sent: boolean }>({
      method: 'POST',
      url: '/api/v1/auth/resend-verification',
      data,
    });
  }

  // New profile-based authentication methods
  async getUserProfiles(username: string): Promise<UserProfilesResponse> {
    return this.request<UserProfilesResponse>({
      method: 'GET',
      url: `/api/v1/auth/user-profiles/${encodeURIComponent(username)}`,
    });
  }

  async getUserInstitutions(username: string): Promise<UserInstitutionsResponse> {
    return this.request<UserInstitutionsResponse>({
      method: 'GET',
      url: `/api/v1/auth/user-institutions/${encodeURIComponent(username)}`,
    });
  }

  async loginWithProfile(credentials: LoginRequest & {
    profile?: string;
    institution_id?: string;
  }): Promise<LoginResponse> {
    return this.request<LoginResponse>({
      method: 'POST',
      url: '/api/v1/auth/login-with-profile',
      data: credentials,
    });
  }

  async getInstitutions(): Promise<{ success: boolean; institutions: any[] }> {
    return this.request<{ success: boolean; institutions: any[] }>({
      method: 'GET',
      url: '/api/v1/auth/institutions',
    });
  }
}

export const authAPI = new AuthAPI();
